import { useCallback, useEffect, useMemo, useState } from 'react';
import type { BriefingJournalPaperMark, BriefingJournalResponse, BriefingJournalRow } from '@railroute/contracts/core';
import { ApiError, fetchBriefingJournal, fetchBriefingJournalPaperMark, fetchMemberships } from '../api';
import {
  JOURNAL_COLUMNS,
  JOURNAL_TITLE,
  defaultPeriod,
  journalCells,
  journalHeader,
  journalSheet,
} from '../briefing-journal/journal';
import {
  KIND_BADGE,
  KIND_FILTERS,
  NO_FILTER,
  conductorLines,
  failed,
  journalCounts,
  kindCounts,
  kindNote,
  screenDate,
  screenRows,
  signatureFootnote,
  signatureLines,
  workerSubline,
  type JournalScreenFilter,
} from '../briefing-journal/screen';
import { XLSX_MIME, buildXlsx } from '../briefing-journal/xlsx';

// Журнал регистрации инструктажа по безопасности и охране труда (issue #533),
// форма приложения 17 Правил № 1019.
//
// ЗАПИСИ — СЕРВЕРНЫЕ. Это те же подписи, что в наряде (issue #531), а не
// отдельный ввод: провели инструктаж в телефоне — строка появилась здесь.
//
// ЭКРАН И БЛАНК — РАЗНОЕ (ночная проверка стенда 21.10.2026). Печать (PDF из
// окна браузера) и Excel — строго форма приложения 17: десять граф из
// `briefing-journal/journal.ts`, без изменений; на бумагу идёт только блок
// `print-only`. Экран собран для чтения (`briefing-journal/screen.ts`):
// шесть колонок, «не прошёл» выделен, способ подписи — абзацем под таблицей.
//
// КОГО ВИДНО, РЕШАЕТ СЕРВЕР: инженер по ОТ — всю организацию, руководитель
// подразделения — своё. Отбор по подразделению здесь только спрашивает; чужое
// подразделение сервер не отдаст. Перечень подразделений берётся из списка
// персонала — у раздела то же право, что у журнала.

type SubdivisionOption = { id: string; name: string };

export function BriefingJournalSection({
  token,
  organizationId,
  onUnauthorized,
  onOpenWorkOrder,
}: {
  token: string;
  organizationId: string;
  onUnauthorized: () => void;
  /**
   * Решение Штаба 30.09.2026: переход к наряду из строки целевого инструктажа.
   * Номер наряда сервер отдаёт только тому, кому виден сам наряд (#554);
   * остальным строка приходит без номера, и перехода у неё нет. Передаётся,
   * только если у смотрящего открыт раздел «Наряд-допуск».
   */
  onOpenWorkOrder?: (workOrderNumber: string) => void;
}) {
  const initial = useMemo(() => defaultPeriod(new Date()), []);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [subdivisionId, setSubdivisionId] = useState('');
  const [subdivisions, setSubdivisions] = useState<SubdivisionOption[]>([]);
  const [journal, setJournal] = useState<BriefingJournalResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<JournalScreenFilter>(NO_FILTER);
  const [photo, setPhoto] = useState<BriefingJournalPaperMark | 'loading' | { error: string } | null>(null);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setJournal(
        await fetchBriefingJournal(token, organizationId, { from, to, subdivisionId: subdivisionId || undefined }),
      );
    } catch (e) {
      setJournal(null);
      if (e instanceof ApiError && e.status === 401) onUnauthorized();
      setError(e instanceof Error ? e.message : 'Журнал не открылся');
    } finally {
      setBusy(false);
    }
  }, [token, organizationId, from, to, subdivisionId, onUnauthorized]);

  useEffect(() => {
    void load();
    // Первое открытие — за период по умолчанию; дальше — по кнопке.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, organizationId]);

  useEffect(() => {
    void fetchMemberships(token, organizationId)
      .then((rows) => {
        const seen = new Map<string, string>();
        for (const row of rows) {
          if (row.subdivisionId && row.subdivisionName) seen.set(row.subdivisionId, row.subdivisionName);
        }
        setSubdivisions(
          [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'ru')),
        );
      })
      .catch(() => setSubdivisions([]));
  }, [token, organizationId]);

  const downloadExcel = () => {
    if (!journal) return;
    const bytes = buildXlsx('Журнал инструктажа', journalSheet(journal), [12, 30, 10, 34, 18, 18, 30, 18, 24, 24]);
    const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: XLSX_MIME }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `Журнал инструктажа ${journal.from} — ${journal.to}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const openPhoto = async (paperMarkId: string) => {
    setPhoto('loading');
    try {
      setPhoto(await fetchBriefingJournalPaperMark(token, organizationId, paperMarkId));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) onUnauthorized();
      setPhoto({ error: e instanceof Error ? e.message : 'Фото не открылось' });
    }
  };

  const rows = journal?.rows ?? [];
  const counts = journalCounts(rows);
  const byKind = kindCounts(rows);
  const shown = screenRows(rows, filter);
  const footnote = signatureFootnote(rows);
  const toggle = (key: 'onlyFailed' | 'onlyUnsigned') => setFilter((f) => ({ ...f, [key]: !f[key] }));

  return (
    <div className="briefing-journal">
      <header className="bj-head screen-only">
        <div>
          <h2 className="section__title">Журнал инструктажа</h2>
          <p className="section__hint">Печать — строго по форме приложения 17 Правил № 1019.</p>
        </div>
        <div className="bj-head__actions">
          <button type="button" className="ui-btn" onClick={() => window.print()} disabled={!journal}>
            Печать формы (PDF)
          </button>
          <button type="button" className="ui-btn" onClick={downloadExcel} disabled={!journal}>
            Выгрузить в Excel
          </button>
        </div>
      </header>

      <div className="bj-filters screen-only">
        <label>
          С
          <input className="ui-input" type="date" value={from} max={to} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label>
          по
          <input className="ui-input" type="date" value={to} min={from} onChange={(event) => setTo(event.target.value)} />
        </label>
        {subdivisions.length > 1 ? (
          <label>
            Подразделение
            <select className="ui-input" value={subdivisionId} onChange={(event) => setSubdivisionId(event.target.value)}>
              <option value="">— все, что мне видны —</option>
              {subdivisions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <button type="button" className="ui-btn" onClick={() => void load()} disabled={busy || !from || !to}>
          Показать
        </button>
        <input
          className="ui-input bj-filters__search"
          type="search"
          placeholder="Поиск по ФИО"
          value={filter.query}
          onChange={(event) => setFilter((f) => ({ ...f, query: event.target.value }))}
          aria-label="Поиск по ФИО"
        />
      </div>

      {error ? <p className="ui-error panel__error screen-only">{error}</p> : null}
      {busy && !journal ? <p className="section__note screen-only">Загрузка…</p> : null}

      {journal ? (
        <div className="screen-only">
          <div className="bj-cards">
            <div className="bj-card">
              <span className="bj-card__value">{counts.total}</span>
              <span className="bj-card__label">записей за период</span>
            </div>
            <div className="bj-card bj-card--danger">
              <span className="bj-card__value">{counts.failed}</span>
              <span className="bj-card__label">не прошли проверку знаний</span>
            </div>
            <div className="bj-card bj-card--warn">
              <span className="bj-card__value">{counts.awaitingWorker}</span>
              <span className="bj-card__label">ждут подписи работника</span>
            </div>
            <div className="bj-card">
              <span className="bj-card__value">{counts.onPaper}</span>
              <span className="bj-card__label">подписано на бумаге, есть фото</span>
            </div>
          </div>

          <div className="personnel__counters bj-kinds" role="group" aria-label="Отбор по виду инструктажа">
            {KIND_FILTERS.map((k) => (
              <button
                key={k.value}
                type="button"
                className={`counter counter--problems${filter.kind === k.value ? ' counter--active' : ''}`}
                aria-pressed={filter.kind === k.value}
                onClick={() => setFilter((f) => ({ ...f, kind: k.value }))}
              >
                {k.label} · {byKind[k.value]}
              </button>
            ))}
            <button
              type="button"
              className={`counter counter--danger${filter.onlyFailed ? ' counter--active' : ''}`}
              aria-pressed={filter.onlyFailed}
              onClick={() => toggle('onlyFailed')}
            >
              Только «не прошёл»
            </button>
            <button
              type="button"
              className={`counter counter--warn${filter.onlyUnsigned ? ' counter--active' : ''}`}
              aria-pressed={filter.onlyUnsigned}
              onClick={() => toggle('onlyUnsigned')}
            >
              Только без подписи
            </button>
          </div>

          {shown.length === 0 ? (
            <p className="section__note">
              {rows.length === 0 ? 'За этот период записей нет.' : 'В этом отборе записей нет.'}{' '}
              {rows.length > 0 ? (
                <button type="button" className="link-btn" onClick={() => setFilter(NO_FILTER)}>
                  Показать все
                </button>
              ) : null}
            </p>
          ) : (
            <>
              <div className="bj-scroll">
                <table className="table bj-table">
                  <thead>
                    <tr>
                      <th>Дата</th>
                      <th>Работник</th>
                      <th>Вид инструктажа</th>
                      <th>Провёл</th>
                      <th>Проверка знаний</th>
                      <th>Подписи</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((row, index) => (
                      <tr key={rowKey(row, index)} className={failed(row) ? 'bj-row--failed' : undefined}>
                        <td className="bj-nowrap">{screenDate(row.date)}</td>
                        <td><WorkerCell row={row} /></td>
                        <td><KindCell row={row} onOpenWorkOrder={onOpenWorkOrder} /></td>
                        <td><ConductorCell row={row} /></td>
                        <td><KnowledgeBadge row={row} /></td>
                        <td><Signatures row={row} onOpenPhoto={openPhoto} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ul className="bj-list">
                {shown.map((row, index) => (
                  <li key={rowKey(row, index)} className={`bj-item${failed(row) ? ' bj-row--failed' : ''}`}>
                    <div className="bj-item__top">
                      <span className="bj-nowrap">{screenDate(row.date)}</span>
                      <KnowledgeBadge row={row} />
                    </div>
                    <WorkerCell row={row} />
                    <KindCell row={row} onOpenWorkOrder={onOpenWorkOrder} />
                    <div className="bj-item__line">
                      <span className="bj-item__label">Провёл</span>
                      <ConductorCell row={row} />
                    </div>
                    <Signatures row={row} onOpenPhoto={openPhoto} />
                  </li>
                ))}
              </ul>
            </>
          )}

          {footnote.length > 0 ? (
            <p className="section__note bj-footnote">{footnote.join(' ')}</p>
          ) : null}
        </div>
      ) : null}

      {/* На бумагу — только бланк приложения 17, ровно как прежде. */}
      {journal ? (
        <div className="print-only">
          <h3 className="briefing-journal__title">{JOURNAL_TITLE}</h3>
          <dl className="briefing-journal__head">
            {journalHeader(journal).map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          <table className="briefing-journal__lines">
            <thead>
              <tr>
                {JOURNAL_COLUMNS.map((title, index) => (
                  <th key={title}>
                    <span className="briefing-journal__no">{index + 1}</span>
                    {title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {journal.rows.length === 0 ? (
                <tr>
                  <td colSpan={JOURNAL_COLUMNS.length}>
                    <em>За этот период записей нет.</em>
                  </td>
                </tr>
              ) : (
                journal.rows.map((row, index) => (
                  <tr key={rowKey(row, index)}>
                    {journalCells(row).map((value, column) => (
                      <td key={column}>{value}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {photo ? <PhotoViewer photo={photo} onClose={() => setPhoto(null)} /> : null}
    </div>
  );
}

function rowKey(row: BriefingJournalRow, index: number): string {
  return `${row.workOrderId ?? `ж${row.journalNumber}`}-${row.briefedUserId}-${row.date}-${index}`;
}

function WorkerCell({ row }: { row: BriefingJournalRow }) {
  const sub = workerSubline(row);
  return (
    <div className="bj-person">
      <strong>{row.briefedName}</strong>
      {sub ? <span className="bj-sub">{sub}</span> : null}
    </div>
  );
}

function KindCell({ row, onOpenWorkOrder }: { row: BriefingJournalRow; onOpenWorkOrder?: (n: string) => void }) {
  const badge = KIND_BADGE[row.briefingKind];
  const note = kindNote(row);
  return (
    <div className="bj-kind">
      <span className={`bj-badge bj-badge--${badge.tone}`}>{badge.label}</span>
      {note.reason ? <span className="bj-sub">{note.reason}</span> : null}
      {note.workOrderNumber ? (
        onOpenWorkOrder ? (
          <button type="button" className="link-btn bj-order" onClick={() => onOpenWorkOrder(note.workOrderNumber as string)}>
            наряд № {note.workOrderNumber}
          </button>
        ) : (
          <span className="bj-sub">наряд № {note.workOrderNumber}</span>
        )
      ) : null}
    </div>
  );
}

function ConductorCell({ row }: { row: BriefingJournalRow }) {
  const { name, position } = conductorLines(row);
  return (
    <div className="bj-person">
      <span>{name || '—'}</span>
      {position ? <span className="bj-sub">{position}</span> : null}
    </div>
  );
}

function KnowledgeBadge({ row }: { row: BriefingJournalRow }) {
  if (row.knowledgePassed === true) return <span className="bj-badge bj-badge--ok">Прошёл</span>;
  if (row.knowledgePassed === false) return <span className="bj-badge bj-badge--danger">Не прошёл</span>;
  return <span className="bj-sub">не записано</span>;
}

function Signatures({ row, onOpenPhoto }: { row: BriefingJournalRow; onOpenPhoto: (id: string) => void }) {
  return (
    <div className="bj-signs">
      {signatureLines(row).map((line, i) =>
        line.paperMarkId ? (
          <button key={i} type="button" className={`link-btn bj-sign bj-sign--${line.tone}`} onClick={() => onOpenPhoto(line.paperMarkId as string)}>
            {line.text}
          </button>
        ) : (
          <span key={i} className={`bj-sign bj-sign--${line.tone}`}>
            {line.text}
          </span>
        ),
      )}
    </div>
  );
}

function PhotoViewer({
  photo,
  onClose,
}: {
  photo: BriefingJournalPaperMark | 'loading' | { error: string };
  onClose: () => void;
}) {
  return (
    <div className="bj-photo screen-only" role="dialog" aria-modal="true" aria-label="Фото бумажного журнала" onClick={onClose}>
      <div className="bj-photo__box" onClick={(event) => event.stopPropagation()}>
        <div className="bj-photo__head">
          <strong>Фото бумажного журнала</strong>
          <button type="button" className="ui-btn" onClick={onClose}>
            Закрыть
          </button>
        </div>
        {photo === 'loading' ? <p className="section__note">Загружаю фото…</p> : null}
        {typeof photo === 'object' && 'error' in photo ? <p className="ui-error">{photo.error}</p> : null}
        {typeof photo === 'object' && 'pages' in photo ? (
          <>
            <p className="section__note">
              {screenDate(photo.briefingDay)}
              {photo.uploadedByName ? ` · загрузил ${photo.uploadedByName}` : ''}
            </p>
            {photo.pages.map((page) => (
              <img
                key={page.pageNumber}
                className="bj-photo__page"
                src={`data:${page.mimeType};base64,${page.contentBase64}`}
                alt={`Страница ${page.pageNumber}`}
              />
            ))}
          </>
        ) : null}
      </div>
    </div>
  );
}
